import type { RolDeUsuario } from '../servicios/api'

/**
 * Todo el texto y la definición del recorrido guiado de lo básico (RF-1701), en un solo lugar para que el
 * contador del piloto lo revise completo (RN-67: los textos viven en el código, junto a la función).
 *
 * Cada paso apunta a un elemento por su atributo `data-recorrido`: un nombre estable que no cambia cuando la
 * pantalla se rediseña. Una prueba (`contenidoDelRecorrido.test.ts`) falla si un paso apunta a un atributo
 * que no existe en el código de las páginas.
 *
 * RN-65: un paso con `roles` solo lo ve quien puede hacer lo que enseña. Las reglas son las del backend:
 * - Solicitar la descarga masiva, generar el catálogo, generar los archivos del Anexo 24 y registrar
 *   declaraciones: Administrador y Contador.
 * - Importar CFDI, contabilizar y registrar pólizas: también Auxiliar.
 * - Consultar: todos.
 * El Administrador ve lo mismo que el Contador (decisión del propietario, 21/10/2026).
 */

/** Sube cuando el recorrido cambie de forma sustancial: vuelve a ofrecerse una vez a todos (RN-66). */
export const VERSION_DEL_RECORRIDO = 1

/** La clave con que se registra en el servidor (RF-1707). */
export const CLAVE_DEL_RECORRIDO = 'basico'

export type TemaDelRecorrido = 'cfdi' | 'contabilidad' | 'obligaciones'

export interface TemaDeAyuda {
  clave: TemaDelRecorrido
  titulo: string
}

export const TEMAS_DEL_RECORRIDO: TemaDeAyuda[] = [
  { clave: 'cfdi', titulo: 'CFDI del SAT' },
  { clave: 'contabilidad', titulo: 'Catálogo, pólizas y reportes' },
  { clave: 'obligaciones', titulo: 'Obligaciones del mes' },
]

export interface PasoDelRecorrido {
  id: string
  /** Null: bienvenida, elegir cliente y cierre, que solo van en el recorrido completo. */
  tema: TemaDelRecorrido | null
  titulo: string
  texto: string
  /** Pantalla que se abre antes del paso; `:cliente` se sustituye por el cliente activo. Null: donde esté. */
  ruta: string | null
  /** Valor de `data-recorrido` del elemento que se resalta. Null: tarjeta centrada, sin resaltado. */
  destino: string | null
  /** Quién lo ve. Sin la propiedad, todos los roles. */
  roles?: RolDeUsuario[]
}

const QUIEN_ADMINISTRA: RolDeUsuario[] = ['Administrador', 'Contador']
const QUIEN_CAPTURA: RolDeUsuario[] = ['Administrador', 'Contador', 'Auxiliar']
const QUIEN_CONSULTA_SIN_ADMINISTRAR: RolDeUsuario[] = ['Auxiliar', 'SoloLectura']

/** En el orden del recorrido completo. Un recorrido por tema toma los suyos en este mismo orden. */
export const PASOS_DEL_RECORRIDO: PasoDelRecorrido[] = [
  {
    id: 'bienvenida',
    tema: null,
    titulo: 'Te damos la bienvenida a ContaQuick',
    texto:
      'En unos pasos te mostramos lo básico: traer los CFDI del SAT, llevar la contabilidad y cumplir las ' +
      'obligaciones del mes. Avanza con «Siguiente» o con las flechas del teclado; ciérralo con Esc cuando quieras.',
    ruta: '/',
    destino: null,
  },
  {
    id: 'tablero-semaforo',
    tema: 'obligaciones',
    titulo: 'El semáforo de cumplimiento',
    texto:
      'Aquí ves de un vistazo qué clientes van al día y cuáles tienen una obligación por vencer o vencida. ' +
      'Cada renglón de abajo dice qué falta y te lleva a la pantalla donde se resuelve.',
    ruta: '/',
    destino: 'tablero-semaforo',
  },
  {
    id: 'selector-de-cliente',
    tema: null,
    titulo: 'Elige el cliente',
    texto:
      'Casi todo se trabaja sobre un cliente. Elígelo aquí: las secciones de la barra lateral se abren sobre él. ' +
      'Sigamos con el que tienes elegido.',
    ruta: null,
    destino: 'selector-de-cliente',
  },
  {
    id: 'cfdi-descargar',
    tema: 'cfdi',
    titulo: 'Trae los CFDI del SAT',
    texto:
      'Pide aquí la descarga masiva por rango de fechas. ContaQuick la presenta y la verifica sola con la ' +
      'e.firma del cliente, y te avisa en la campana cuando termina.',
    ruta: '/clientes/:cliente/cfdi',
    destino: 'cfdi-descargar',
    roles: QUIEN_ADMINISTRA,
  },
  {
    id: 'cfdi-importar',
    tema: 'cfdi',
    titulo: '¿Tienes los XML a la mano?',
    texto:
      'Impórtalos aquí, sueltos o en un ZIP. Un CFDI repetido no se duplica: se reconoce por su UUID.',
    ruta: '/clientes/:cliente/cfdi',
    destino: 'cfdi-importar',
    roles: QUIEN_CAPTURA,
  },
  {
    id: 'cfdi-repositorio',
    tema: 'cfdi',
    titulo: 'El repositorio del cliente',
    texto:
      'Aquí quedan sus comprobantes emitidos y recibidos, con el XML original intacto. Fíltralos, revisa su ' +
      'estatus ante el SAT y descárgalos cuando los necesites.',
    ruta: '/clientes/:cliente/cfdi',
    destino: 'cfdi-repositorio',
  },
  {
    id: 'contabilizacion-filtros',
    tema: 'cfdi',
    titulo: 'Lo que falta contabilizar',
    texto:
      'Cada CFDI entra aquí como pendiente. Filtra por estado para ver qué falta y qué ya tiene póliza.',
    ruta: '/clientes/:cliente/contabilizacion',
    destino: 'contabilizacion-filtros',
  },
  {
    id: 'contabilizacion-lote',
    tema: 'cfdi',
    titulo: 'Contabiliza en lote',
    texto:
      'Selecciona varios CFDI y genera sus pólizas de una vez. ContaQuick propone las cuentas con las reglas ' +
      'del cliente; tú las revisas y las confirmas.',
    ruta: '/clientes/:cliente/contabilizacion',
    destino: 'contabilizacion-lote',
    roles: QUIEN_CAPTURA,
  },
  {
    id: 'catalogo',
    tema: 'contabilidad',
    titulo: 'El catálogo de cuentas',
    texto:
      'Si el cliente todavía no tiene catálogo, genéralo desde la plantilla de su régimen: cada cuenta ya trae ' +
      'su código agrupador del SAT. Después ajústalo a su operación.',
    ruta: '/clientes/:cliente/catalogo',
    destino: 'catalogo',
    roles: QUIEN_ADMINISTRA,
  },
  {
    id: 'catalogo-consulta',
    tema: 'contabilidad',
    titulo: 'El catálogo de cuentas',
    texto:
      'Aquí consultas las cuentas del cliente con su código agrupador del SAT. Quien da de alta y ajusta las ' +
      'cuentas es el contador.',
    ruta: '/clientes/:cliente/catalogo',
    destino: 'catalogo',
    roles: QUIEN_CONSULTA_SIN_ADMINISTRAR,
  },
  {
    id: 'polizas-nueva',
    tema: 'contabilidad',
    titulo: 'Registra una póliza',
    texto:
      'Captura aquí pólizas de ingreso, egreso o diario. Si una se repite cada mes, guárdala como plantilla ' +
      'recurrente.',
    ruta: '/clientes/:cliente/polizas',
    destino: 'polizas-nueva',
    roles: QUIEN_CAPTURA,
  },
  {
    id: 'polizas-periodos',
    tema: 'contabilidad',
    titulo: 'Las pólizas, mes por mes',
    texto:
      'Cambia de periodo aquí. Un periodo cerrado ya no admite cambios hasta que alguien con permiso lo reabre.',
    ruta: '/clientes/:cliente/polizas',
    destino: 'polizas-periodos',
  },
  {
    id: 'reportes-pestanas',
    tema: 'contabilidad',
    titulo: 'Balanza y estados financieros',
    texto:
      'Consulta la balanza, el libro mayor, el diario, el balance general y el estado de resultados del ' +
      'periodo, con su comparativo.',
    ruta: '/clientes/:cliente/reportes',
    destino: 'reportes-pestanas',
  },
  {
    id: 'reportes-exportar',
    tema: 'contabilidad',
    titulo: 'Llévatelos en Excel o PDF',
    texto:
      'Exporta el reporte que tienes abierto, o descarga el paquete de cierre con la balanza, el balance y el ' +
      'estado de resultados en un ZIP.',
    ruta: '/clientes/:cliente/reportes',
    destino: 'reportes-exportar',
  },
  {
    id: 'envios',
    tema: 'obligaciones',
    titulo: 'Contabilidad electrónica (Anexo 24)',
    texto:
      'Genera aquí la balanza y los demás archivos del Anexo 24, ya validados contra el XSD del SAT. Tú los ' +
      'subes al Buzón Tributario y después registras el acuse.',
    ruta: '/clientes/:cliente/control-envios',
    destino: 'envios',
    roles: QUIEN_ADMINISTRA,
  },
  {
    id: 'envios-consulta',
    tema: 'obligaciones',
    titulo: 'Contabilidad electrónica (Anexo 24)',
    texto:
      'Aquí sigues los archivos del Anexo 24 de este cliente: cuáles se generaron, cuáles se enviaron al SAT y ' +
      'qué acuse devolvió.',
    ruta: '/clientes/:cliente/control-envios',
    destino: 'envios',
    roles: QUIEN_CONSULTA_SIN_ADMINISTRAR,
  },
  {
    id: 'declaraciones',
    tema: 'obligaciones',
    titulo: 'Las declaraciones del mes',
    texto:
      'Sigue el IVA, el ISR y la DIOT de cada periodo: si su papel de trabajo está sellado, si ya se presentó y ' +
      'si su acuse está resguardado.',
    ruta: '/clientes/:cliente/declaraciones',
    destino: 'declaraciones',
  },
  {
    id: 'declaraciones-registrar',
    tema: 'obligaciones',
    titulo: 'Registra lo que presentaste',
    texto:
      'Cuando presentes una declaración en el portal del SAT, registra aquí la fecha y el número de operación, ' +
      'y resguarda el acuse: así el tablero la da por cumplida. Registrar no presenta nada ante el SAT.',
    ruta: '/clientes/:cliente/declaraciones',
    destino: 'declaraciones-registrar',
    roles: QUIEN_ADMINISTRA,
  },
  {
    id: 'cierre',
    tema: null,
    titulo: 'Eso es lo básico',
    texto:
      'Vuelve a ver este recorrido, completo o por tema, desde «Ayuda». Ahí también apagas los TIPs si ya no ' +
      'los necesitas.',
    ruta: null,
    destino: 'menu-ayuda',
  },
]

/** Los textos fijos de la tarjeta del recorrido. */
export const TEXTOS_DEL_RECORRIDO = {
  anterior: 'Anterior',
  siguiente: 'Siguiente',
  terminar: 'Terminar',
  omitir: 'Omitir recorrido',
  progreso: (paso: number, total: number) => `Paso ${paso} de ${total}`,
}

/** Los textos del menú «Ayuda» (RF-1702 y el interruptor de RF-1707). */
export const TEXTOS_DEL_MENU_DE_AYUDA = {
  boton: 'Ayuda',
  tituloDelMenu: 'Ayuda',
  seccionRecorrido: 'Recorrido guiado',
  recorridoCompleto: 'Recorrido completo',
  mostrarTips: 'Mostrar TIPs',
  explicacionDeTips:
    'Los TIPs salen la primera vez que abres una pantalla, en el tablero y con cada novedad. La ayuda junto a ' +
    'los campos sigue disponible aunque los apagues.',
  sinPreferencias: 'Todavía no se cargan tus preferencias de ayuda. Espera un momento o recarga la página.',
  errorAlGuardar: 'No se pudo guardar tu preferencia de TIPs. Revisa tu conexión y vuelve a intentarlo.',
}

/** El paso que sustituye a las pantallas de un cliente cuando no hay ninguno elegido. */
export const PASO_SIN_CLIENTE: PasoDelRecorrido = {
  id: 'sin-cliente',
  tema: null,
  titulo: 'Primero elige un cliente',
  texto:
    'Esta parte del recorrido se hace sobre un cliente. Elige uno en la barra lateral, o da de alta el primero ' +
    'en Clientes, y vuelve a lanzar el recorrido desde «Ayuda».',
  ruta: null,
  destino: null,
}

const esDeCliente = (paso: PasoDelRecorrido) => paso.ruta?.includes(':cliente') ?? false

/**
 * Los pasos de una corrida: los de un tema o, con `tema` null, el recorrido completo. Quita lo que el rol no
 * puede hacer (RN-65); un rol desconocido solo ve lo que ven todos. Sin cliente, las pantallas de un cliente
 * se sustituyen por un solo paso que pide elegirlo, y el de elegir cliente sobra.
 */
export function pasosDelRecorrido(
  tema: TemaDelRecorrido | null, rol: RolDeUsuario | null, hayCliente: boolean,
): PasoDelRecorrido[] {
  const visibles = PASOS_DEL_RECORRIDO.filter(
    (p) => (tema === null || p.tema === tema) && (!p.roles || (rol !== null && p.roles.includes(rol))),
  )
  if (hayCliente) return visibles

  const resultado: PasoDelRecorrido[] = []
  for (const paso of visibles) {
    if (paso.id === 'selector-de-cliente') continue
    if (!esDeCliente(paso)) resultado.push(paso)
    else if (!resultado.includes(PASO_SIN_CLIENTE)) resultado.push(PASO_SIN_CLIENTE)
  }
  return resultado
}

/** La ruta de un paso con el cliente puesto; null si el paso no abre ninguna pantalla. */
export function rutaDelPaso(paso: PasoDelRecorrido, clienteId: string): string | null {
  return paso.ruta === null ? null : paso.ruta.replace(':cliente', encodeURIComponent(clienteId))
}
